import { Injectable } from '@angular/core';
import { Subject, Observable } from 'rxjs';
import { Firestore, collection, addDoc, getDocs, query, orderBy, Timestamp } from '@angular/fire/firestore';
import { AppointmentData } from '../components/shared/appointment-modal/appointment-modal';

export interface Appointment {
  id: string;
  name: string;
  age: number | null;
  gender: string;
  whatsappNumber: string;
  address: string;
  createdAt: Date | Timestamp | any;
  status: string;
}

@Injectable({
  providedIn: 'root'
})
export class AppointmentService {
  private openModalSubject = new Subject<void>();
  private readonly APPOINTMENTS_COLLECTION = 'appointments';
  
  // Observable for components to subscribe to
  openModal$ = this.openModalSubject.asObservable();
  
  constructor(private firestore: Firestore) {}
  
  // Method to trigger modal opening
  openAppointmentModal(): void {
    this.openModalSubject.next();
  }

  // Save appointment to Firebase
  saveAppointment(appointmentData: AppointmentData): Observable<{ success: boolean; message: string; appointmentId?: string }> {
    const appointmentRecord = {
      name: appointmentData.name,
      age: appointmentData.age,
      gender: appointmentData.gender,
      whatsappNumber: appointmentData.whatsappNumber,
      address: appointmentData.address,
      createdAt: new Date(),
      status: 'pending' // pending, confirmed, cancelled
    };

    const appointmentsRef = collection(this.firestore, this.APPOINTMENTS_COLLECTION);
    
    return new Observable(observer => {
      addDoc(appointmentsRef, appointmentRecord)
        .then((docRef) => {
          console.log('Appointment saved with ID:', docRef.id);
          observer.next({
            success: true,
            message: 'Appointment saved successfully',
            appointmentId: docRef.id
          });
          observer.complete();
        })
        .catch((error) => {
          console.error('Error saving appointment:', error);
          observer.error({
            success: false,
            message: 'Failed to save appointment: ' + error.message
          });
        });
    });
  }

  // Get all appointments from Firebase
  getAllAppointments(): Observable<Appointment[]> {
    const appointmentsRef = collection(this.firestore, this.APPOINTMENTS_COLLECTION);
    const q = query(appointmentsRef, orderBy('createdAt', 'desc'));
    
    return new Observable(observer => {
      getDocs(q)
        .then((querySnapshot) => {
          const appointments: Appointment[] = [];
          querySnapshot.forEach((doc) => {
            const data = doc.data();
            appointments.push({
              id: doc.id,
              name: data['name'] || '',
              age: data['age'] || null,
              gender: data['gender'] || '',
              whatsappNumber: data['whatsappNumber'] || '',
              address: data['address'] || '',
              createdAt: data['createdAt'],
              status: data['status'] || 'pending'
            });
          });
          observer.next(appointments);
          observer.complete();
        })
        .catch((error) => {
          console.error('Error fetching appointments:', error);
          observer.error(error);
        });
    });
  }
}
